<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
    // { attribute, condition, value }
  },
  sources: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(['update:modelValue', 'remove']);
const { t } = useI18n();

const ATTRIBUTE_OPTIONS = computed(() => [
  { label: t('CATALOG.PRODUCT_SETS.FILTER.ATTRIBUTES.title'), value: 'title' },
  { label: t('CATALOG.PRODUCT_SETS.FILTER.ATTRIBUTES.status'), value: 'status' },
  { label: t('CATALOG.PRODUCT_SETS.FILTER.ATTRIBUTES.vendor'), value: 'vendor' },
  { label: t('CATALOG.PRODUCT_SETS.FILTER.ATTRIBUTES.product_type'), value: 'product_type' },
  { label: t('CATALOG.PRODUCT_SETS.FILTER.ATTRIBUTES.source'), value: 'source' },
]);

const STRING_CONDITIONS = computed(() => [
  { label: t('CATALOG.PRODUCT_SETS.FILTER.CONDITIONS.contains'), value: 'contains' },
  { label: t('CATALOG.PRODUCT_SETS.FILTER.CONDITIONS.not_contains'), value: 'not_contains' },
  { label: t('CATALOG.PRODUCT_SETS.FILTER.CONDITIONS.equals'), value: 'equals' },
  { label: t('CATALOG.PRODUCT_SETS.FILTER.CONDITIONS.not_equals'), value: 'not_equals' },
  { label: t('CATALOG.PRODUCT_SETS.FILTER.CONDITIONS.starts_with'), value: 'starts_with' },
]);

const EQUALITY_CONDITIONS = computed(() => [
  { label: t('CATALOG.PRODUCT_SETS.FILTER.CONDITIONS.equals'), value: 'equals' },
  { label: t('CATALOG.PRODUCT_SETS.FILTER.CONDITIONS.not_equals'), value: 'not_equals' },
]);

const STATUS_OPTIONS = computed(() => [
  { label: t('CATALOG.PRODUCTS.STATUS.active'), value: 'active' },
  { label: t('CATALOG.PRODUCTS.STATUS.draft'), value: 'draft' },
  { label: t('CATALOG.PRODUCTS.STATUS.archived'), value: 'archived' },
]);

const conditionOptions = computed(() => {
  if (['status', 'source'].includes(props.modelValue.attribute)) return EQUALITY_CONDITIONS.value;
  return STRING_CONDITIONS.value;
});

const sourceOptions = computed(() =>
  props.sources.map(s => ({ label: s.name || s.reference || s.kind, value: String(s.id) }))
);

const isSelectValue = computed(() =>
  props.modelValue.attribute === 'status' || props.modelValue.attribute === 'source'
);

const valueSelectOptions = computed(() => {
  if (props.modelValue.attribute === 'status') return STATUS_OPTIONS.value;
  if (props.modelValue.attribute === 'source') return sourceOptions.value;
  return [];
});

const attributeLabel = computed(
  () => ATTRIBUTE_OPTIONS.value.find(o => o.value === props.modelValue.attribute)?.label || ''
);
const conditionLabel = computed(
  () => conditionOptions.value.find(o => o.value === props.modelValue.condition)?.label || ''
);
const valueLabel = computed(
  () => valueSelectOptions.value.find(o => o.value === props.modelValue.value)?.label || props.modelValue.value || ''
);

const update = patch => emit('update:modelValue', { ...props.modelValue, ...patch });

const onAttributeChange = val => {
  update({ attribute: val, condition: 'equals', value: '' });
};
</script>

<template>
  <div class="flex items-center gap-2">
    <SelectMenu
      :model-value="modelValue.attribute"
      :options="ATTRIBUTE_OPTIONS"
      :label="attributeLabel"
      sub-menu-position="bottom"
      class="w-36 shrink-0"
      @update:model-value="onAttributeChange"
    />
    <SelectMenu
      :model-value="modelValue.condition"
      :options="conditionOptions"
      :label="conditionLabel"
      sub-menu-position="bottom"
      class="w-36 shrink-0"
      @update:model-value="val => update({ condition: val })"
    />
    <SelectMenu
      v-if="isSelectValue"
      :model-value="modelValue.value"
      :options="valueSelectOptions"
      :label="valueLabel || '—'"
      sub-menu-position="bottom"
      class="w-40 shrink-0"
      @update:model-value="val => update({ value: val })"
    />
    <Input
      v-else
      :model-value="modelValue.value"
      class="flex-1"
      :custom-input-class="['h-8']"
      @update:model-value="val => update({ value: val })"
    />
    <Button
      icon="i-lucide-x"
      color="slate"
      size="sm"
      variant="ghost"
      class="shrink-0"
      @click="$emit('remove')"
    />
  </div>
</template>
